import { ParamsType } from 'rcx_adapter';
import i18n from 'rcx_i18n';

import { BaseLog } from './BaseLog';
import { ServiceNowEntity } from '../../enums';
import { catchErrorAndLog } from '../../utils/catchError';
import { formatDate, formatDuration } from '../../utils/timeFormat';
import type { SNSaveLogOptions } from '../ServiceNowAdapter.interface';

export class IncidentLog extends BaseLog {
    private async openIncident(sys_id: string, params: { [k: string]: any }) {
        if (sys_id !== '-1') {
            await this.updateIncident(sys_id, params);
            this.viewLog(sys_id);
            return;
        }
        const query = Object.entries(params)
            .map(([key, value]) => `${key}=${value}`)
            .join('^');
        this.adapter.openFrameOpenServiceNowForm(ServiceNowEntity.INCIDENT, `sys_id=${sys_id}&sysparm_query=${query}`);
    }

    @catchErrorAndLog
    private async getIncidentComments(sys_id: string): Promise<string[][]> {
        const response = await this.dataFetch.getIncidentComments(sys_id);
        const comments = response?.result?.[0]?.comments || '';
        const incidentComments = comments
            .split(/\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}\s+-.*?\(Additional comments\)/g)
            .map((comment: string) => comment.trim().split('\n'));
        return incidentComments || [];
    }

    @catchErrorAndLog
    private async getChangedCommentLines(sys_id: string, callIdPart: string, currentComment: string[]) {
        let newComments = currentComment;
        const incidentComments = (await this.getIncidentComments(sys_id)) || [];
        const existingCommentsGroup = incidentComments.filter((comment) =>
            comment.some((commentRow) => commentRow.includes(callIdPart))
        );
        const flatExistComments = [...new Set(existingCommentsGroup.flat())];
        if (flatExistComments.length) {
            newComments = [callIdPart].concat(currentComment.filter((comment) => !flatExistComments.includes(comment)));
        }
        return newComments;
    }

    public async saveLog({ paramsType, storageLogData, callLogSysId }: SNSaveLogOptions) {
        const {
            whoId,
            RCX_Disposition,
            callType,
            subject,
            description,
            RCX_Digital_Type,
            Call_UUID,
            callDurationInSeconds,
            Call_Recording,
            Call_Transcript,
            Call_HandleTime,
            Call_Queue,
            summary,
            leadName,
            logFields,
            isEnded,
        } = storageLogData;
        const callIdPart = i18n.t('common.call_id', { callId: Call_UUID });
        const callDurationPart =
            callDurationInSeconds && isEnded
                ? i18n.t('common.call_duration', { callDuration: formatDuration(callDurationInSeconds) })
                : '';
        const callQueuePart =
            paramsType === ParamsType.CALL && Call_Queue ? i18n.t('common.call_queue', { callQueue: Call_Queue }) : '';
        const callTypePart = callType
            ? i18n.t('common.call_type', { callType: callType.replace(/^\w/, (c) => c.toUpperCase()) })
            : '';
        const startTimePart =
            paramsType === ParamsType.CALL && Call_HandleTime
                ? i18n.t('common.start_time', { startTime: formatDate(new Date(Call_HandleTime)) })
                : '';
        const endTimePart =
            callDurationPart && Call_HandleTime
                ? i18n.t('common.end_time', {
                      endTime: formatDate(new Date(Call_HandleTime + callDurationInSeconds * 1000)),
                  })
                : '';
        const dispositionPart = RCX_Disposition ? i18n.t('common.disposition', { disposition: RCX_Disposition }) : '';
        const notePart = description ? i18n.t('common.note', { notes: description }) : '';
        const leadNamePart = leadName ? i18n.t('common.lead_name', { leadName }) : '';
        const summaryPart = summary ? i18n.t('common.summary', { summary }) : '';
        const recordingPart = Call_Recording ? i18n.t('common.call_recording', { callRecording: Call_Recording }) : '';
        const transcriptPart = Call_Transcript
            ? i18n.t('common.call_transcript', { callTranscript: Call_Transcript })
            : '';
        const digitalTypePart = RCX_Digital_Type
            ? i18n.t('common.digital_type', { channelType: RCX_Digital_Type })
            : '';
        let public_comment = [
            callIdPart,
            callDurationPart,
            callQueuePart,
            callTypePart,
            startTimePart,
            endTimePart,
            dispositionPart,
            notePart,
            summaryPart,
            leadNamePart,
            transcriptPart,
            recordingPart,
            digitalTypePart,
        ].filter(Boolean);

        let sys_id = '-1';
        if (callLogSysId) {
            sys_id = callLogSysId;
        } else if (paramsType === ParamsType.CALL) {
            const data = await this.getLogByCallIds([Call_UUID]);
            const result = data?.result || [];
            if (result.length > 0) {
                sys_id = result[0].sys_id;
            }
        }
        if (sys_id !== '-1') {
            const comments = (await this.getChangedCommentLines(sys_id, callIdPart, public_comment)) || public_comment;
            public_comment = comments;
        }
        const params: any = {
            contact_type: paramsType === ParamsType.CALL ? 'phone' : 'chat',
            short_description: typeof subject === 'string' ? subject : '',
            comments: public_comment.join('\n'),
        };
        if (whoId) {
            params.caller_id = whoId;
        }
        if (sys_id === '-1' && logFields) {
            Object.entries(logFields).forEach(([key, value]) => {
                params[key] = value;
            });
        }
        this.openIncident(sys_id, params);
    }

    public viewLog(sys_id: string) {
        this.adapter.openFrameOpenServiceNowFormBySysId(sys_id, ServiceNowEntity.INCIDENT);
    }

    @catchErrorAndLog
    public getLogByCallIds(ids: string[]) {
        return this.dataFetch.getIncidentByCallIds(ids);
    }

    @catchErrorAndLog
    public updateIncident(id: string, args: { [k: string]: any }) {
        return this.dataFetch.updateIncident(id, args);
    }
}
